import React from 'react'
import { Dialog } from '@mui/material'
import { Close as CloseIcon, ContentCopy as CopyIcon } from '@mui/icons-material'
import { useDispatch } from '../../store'
import { api } from '../../function/axios'
import { postPackage } from '../../store/package/actionCreator'
import { Package } from '../../store/package/reducer'
import { UserPicker } from './UserPicker'
import l from '../../page/SubscribersPageV2.module.css'
import s from './DuplicatePackageDialog.module.css'

/*
 * "Duplicate package" — a copy of a package with only what has to differ:
 * the title ("… (copy)"), the new PayPro products (monthly, and annual when the
 * original has one) and their prices, or for an enterprise / personal package
 * the uid of its new owner. Everything else — features, workflows, type,
 * domain, API limits, flags — is the original's. The copy is created
 * INACTIVE, so no user sees it until it is switched on in Edit.
 *
 * The request body is built field by field the way PopupPackage builds it
 * (component/package/PopupPackage.tsx requestData + handleSubmit), and the new
 * product ids / uid go through the same /package/check first.
 */

/* the classic form allows one custom and one demo package — a copy would be a second */
export const canDuplicate = (p: Package) => p.type !== 'custom' && p.type !== 'demo'

type Errors = Partial<Record<'title' | 'productId' | 'annualProductId' | 'uid' | 'price' | 'annualPrice', string>>

const numOrNull = (v: string): number | null => (v.trim() === '' || !Number.isFinite(Number(v)) ? null : Number(v))

export function DuplicatePackageDialog({ pkg, theme, onClose, onCreated }: {
  pkg: Package
  theme: 'light' | 'dark'
  onClose: () => void
  onCreated: () => void
}) {
  const dispatch = useDispatch()
  const type = pkg.type as string
  const isCustom = type === 'custom'
  const isDemo = type === 'demo'
  const isPersonal = type === 'personal'
  const isGeneral = type === 'general'
  const useUID = isPersonal || type === 'webinthub_demo'
  const isAnnual = !!pkg.isAnnual

  // what the copy asks for
  const askProduct = !isCustom && !pkg.isFree && !isDemo && pkg.productId != null
  const askAnnual = askProduct && isAnnual && !!pkg.annualProductId
  const askPrice = !isCustom

  const [title, setTitle] = React.useState(`${pkg.title} (copy)`)
  const [productId, setProductId] = React.useState('')
  const [annualProductId, setAnnualProductId] = React.useState('')
  const [uid, setUid] = React.useState('')
  const [price, setPrice] = React.useState(String(pkg.price ?? 0))
  const [annualPrice, setAnnualPrice] = React.useState(String(pkg.annualPrice ?? 0))
  const [errors, setErrors] = React.useState<Errors>({})
  const [saving, setSaving] = React.useState(false)

  const validate = (): Errors => {
    const e: Errors = {}
    if (!title.trim()) e.title = 'Enter a title'
    if (askProduct && numOrNull(productId) == null) e.productId = 'Enter the new monthly product ID'
    if (askAnnual && numOrNull(annualProductId) == null) e.annualProductId = 'Enter the new annual product ID'
    if (askAnnual && numOrNull(annualProductId) != null && annualProductId.trim() === productId.trim()) e.annualProductId = 'Must differ from the monthly product'
    if (useUID && !uid.trim()) e.uid = 'Choose the new owner'
    if (askPrice && numOrNull(price) == null) e.price = 'Enter a price'
    if (askPrice && isAnnual && numOrNull(annualPrice) == null) e.annualPrice = 'Enter an annual price'
    return e
  }

  /* /package/check — the classic form's check: does the PayPro product / the user exist */
  const check = async (body: Record<string, string | number>): Promise<{ productError?: string; userError?: string }> => {
    const { data } = await api({ method: 'POST', url: '/package/check', body })
    return data || {}
  }

  const save = async () => {
    const e = validate()
    setErrors(e)
    if (Object.keys(e).length) return
    setSaving(true)
    try {
      const found: Errors = {}
      /*
        /package/check requires a productId (CheckUserIdAndProductIdDto) — a
        uid alone is a 400. So it runs only when the package has a product
        (the uid rides along then); without one — an enterprise package —
        nothing is checked, as when such a package is created in the form.
      */
      if (askProduct) {
        const res = await check({ productId: Number(productId), ...(useUID ? { userId: uid.trim() } : {}) })
        if (res.productError) found.productId = res.productError
        if (res.userError) found.uid = res.userError
      }
      if (askAnnual && !found.productId) {
        const res = await check({ productId: Number(annualProductId) })
        if (res.productError) found.annualProductId = res.productError
      }
      if (Object.keys(found).length) {
        setErrors(found)
        setSaving(false)
        return
      }

      const feature = (f: any) => ({ title: f?.title || '', subTitle: f?.subTitle || '', info: f?.info || '' })
      const body: Record<string, any> = {
        type: pkg.type,
        packageDomain: isCustom || pkg.isFree || isDemo ? null : pkg.packageDomain,
        lookupIds: isCustom ? null : (pkg.lookups || []).map((x) => x.id),
        isFree: !!pkg.isFree,
        isAnnual,
        activeDay: pkg.activeDay ?? null,
        title: title.trim(),
        subTitle: { type: pkg.subTitle?.type || 'text', context: (pkg.subTitle?.context || '').trim() },
        uid: useUID ? uid.trim() : null,
        apiRequestsLimits: isPersonal ? (pkg.apiRequestsLimits || 1) : 0,
        apiRequestsInterval: isPersonal ? (pkg.apiRequestsInterval || 1) : 0,
        productId: askProduct ? Number(productId) : null,
        price: isCustom ? null : numOrNull(price),
        bonus: isCustom ? null : (pkg.bonus || 0),
        creditsPerUnit: isPersonal || isGeneral ? (pkg.creditsPerUnit || 0) : null,
        button: pkg.isFree || isDemo ? null : (pkg.button || '').trim(),
        demoLink: isCustom ? pkg.demoLink : null,
        annualPrice: isAnnual ? (numOrNull(annualPrice) ?? 0) : 0,
        annualProductId: isAnnual ? (askAnnual ? Number(annualProductId) : 0) : 0,
        isActive: false,
        isIrbisPro: pkg.isIrbisPro,
        isBestProposition: pkg.isBestProposition,
        isAiAssistant: pkg.isAiAssistant,
        searchRuleType: pkg.searchRuleType,
        getRequestsCount: pkg.getRequestsCount || 0,
        maxKeysNum: isPersonal ? (pkg.maxKeysNum ?? 0) : 0,
        feature1: feature(pkg.feature1),
        feature2: feature(pkg.feature2),
        feature3: feature(pkg.feature3),
      }
      dispatch(postPackage(body, () => { onCreated(); onClose() }))
    } catch (err) {
      setErrors({ title: (err as Error)?.message || 'Could not check the new values' })
    } finally {
      setSaving(false)
    }
  }

  const field = (key: keyof Errors, label: string, value: string, set: (v: string) => void, opts: { placeholder?: string; mono?: boolean; hint?: string } = {}) => (
    <label className={s.field}>
      <span className={s.label}>{label}</span>
      <input
        className={`${s.input} ${opts.mono ? s.mono : ''} ${errors[key] ? s.inputErr : ''}`}
        value={value}
        placeholder={opts.placeholder}
        inputMode={opts.mono ? 'numeric' : undefined}
        onChange={(e) => { set(e.target.value); setErrors((er) => ({ ...er, [key]: undefined })) }}
      />
      {errors[key] ? <span className={s.err}>{errors[key]}</span> : opts.hint ? <span className={s.hint}>{opts.hint}</span> : null}
    </label>
  )

  return (
    <Dialog open onClose={onClose} maxWidth={false} PaperProps={{ sx: { background: 'transparent', boxShadow: 'none', m: 2 } }}>
      <div className={`${l.theme} ${s.shell}`} data-theme={theme}>
        <div className={s.top}>
          <span className={s.icon}><CopyIcon sx={{ fontSize: 18 }} /></span>
          <div className={s.topText}>
            <b>Duplicate package</b>
            <span>Everything else is copied from “{pkg.title}”. The copy starts inactive.</span>
          </div>
          <button type="button" className={s.close} onClick={onClose} aria-label="Close"><CloseIcon sx={{ fontSize: 18 }} /></button>
        </div>

        <div className={s.body}>
          {field('title', 'Title', title, setTitle)}

          {useUID && (
            <div className={s.field}>
              <span className={s.label}>Owner</span>
              <UserPicker value={uid} onChange={(v) => { setUid(v); setErrors((er) => ({ ...er, uid: undefined })) }} invalid={!!errors.uid} />
              {errors.uid ? <span className={s.err}>{errors.uid}</span> : <span className={s.hint}>Original owner uid: {pkg.uid || '—'}</span>}
            </div>
          )}

          {askProduct && (
            <div className={s.pair}>
              {field('productId', 'Monthly product ID', productId, setProductId, { placeholder: 'New PayPro product', mono: true, hint: `Original: ${pkg.productId}` })}
              {askPrice && field('price', 'Monthly price, €', price, setPrice, { mono: true })}
            </div>
          )}
          {!askProduct && askPrice && field('price', 'Price, €', price, setPrice, { mono: true })}

          {askAnnual && (
            <div className={s.pair}>
              {field('annualProductId', 'Annual product ID', annualProductId, setAnnualProductId, { placeholder: 'New PayPro product', mono: true, hint: `Original: ${pkg.annualProductId}` })}
              {field('annualPrice', 'Annual price, €', annualPrice, setAnnualPrice, { mono: true })}
            </div>
          )}
          {!askAnnual && isAnnual && askPrice && field('annualPrice', 'Annual price, €', annualPrice, setAnnualPrice, { mono: true })}
        </div>

        <div className={s.foot}>
          <button type="button" className={s.ghost} onClick={onClose}>Cancel</button>
          <button type="button" className={s.primary} onClick={() => { void save() }} disabled={saving}>
            {saving ? 'Checking…' : 'Create copy'}
          </button>
        </div>
      </div>
    </Dialog>
  )
}
